import { useAuthContext } from 'context/AuthProvider'
import { CONNECT, JOINED } from 'lib/websockets/constants'
import React from 'react'
import { useAppContext } from '../context/AppProvider'
// Direct import: `lib/index.ts` also re-exports the `world-countries`
// dataset, which has no business being in the initial bundle.
import { connectSocket, disconnectSocket } from '../lib/websockets'

/**
 * Hold the app's single socket connection for the signed-in user.
 *
 * Fixes carried by this version:
 *  - The effect only depended on `isAuthenticated`. Authentication flips true
 *    before `user` is populated on a cold load, so the socket connected with
 *    `userId: undefined`, the server refused to join any room, and realtime
 *    silently never worked until the next full reload. It now waits for — and
 *    re-runs on — the user id.
 *  - `connect_error` / `connect_timeout` called `socket.connect()` by hand
 *    while socket.io's own reconnection was enabled, producing two competing
 *    reconnect loops (and a burst of connections against a server that was
 *    already struggling). The built-in backoff is left to do its job.
 */
const useSocketConnector = () => {
  const { isAuthenticated, user } = useAuthContext()
  const { setIsLive, setConnected } = useAppContext()

  const userId = user?.id

  React.useEffect(() => {
    if (!isAuthenticated || !userId) return

    const socket = connectSocket({ userId })

    const syncStatus = () => {
      setConnected?.(socket.connected)
      setIsLive?.(socket.connected)
    }

    socket.on(CONNECT, syncStatus)
    socket.on(JOINED, syncStatus)
    socket.on('disconnect', syncStatus)

    return () => {
      disconnectSocket()
      setConnected?.(false)
      setIsLive?.(false)
    }
  }, [isAuthenticated, userId, setConnected, setIsLive])
}

export default useSocketConnector
